import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { CATEGORY_LABELS, currency } from "@/lib/format";
import { ArrowRight, Fuel, Gauge, Users, Star, ShieldCheck, Sparkles, CheckCircle2, KeyRound, Eye, Flame, Clock } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { VehicleCard } from "@/components/vehicle/VehicleCard";
import { BookingPlanner } from "@/components/vehicle/BookingPlanner";
import { motion, AnimatePresence } from "framer-motion";

// Deterministic-ish pseudo-random from a string seed
function seedFrom(str) {
  let h = 0;
  for (let i = 0; i < (str || "").length; i++) h = ((h << 5) - h + str.charCodeAt(i)) | 0;
  return Math.abs(h);
}

const NAMES = ["Aisha", "Ravi", "Jordan", "Ming", "Sofia", "Ethan", "Priya", "Luca", "Chloe", "Marcus", "Nadia", "Owen"];
const CITIES = ["Toronto", "Mississauga", "Ottawa", "Hamilton", "London", "Kitchener", "Windsor", "Markham"];

export default function VehicleDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mainImg, setMainImg] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/vehicles/${id}`);
        setVehicle(res.data);
        setMainImg(res.data.image_url);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  // Social urgency values (deterministic + gently varying with time)
  const urgency = useMemo(() => {
    if (!vehicle) return null;
    const s = seedFrom(vehicle.id || "");
    const viewers = 3 + (s % 9);
    const weekly = Math.max(4, (vehicle.booked_this_month || 12) - 2 + (s % 5));
    const minutesAgo = 4 + (s % 55);
    const nameIdx = s % NAMES.length;
    const cityIdx = (s >> 3) % CITIES.length;
    return { viewers, weekly, minutesAgo, name: NAMES[nameIdx], city: CITIES[cityIdx] };
  }, [vehicle]);

  // Rolling ticker of recent bookings
  const ticker = useMemo(() => {
    if (!vehicle || !urgency) return [];
    return [
      `${urgency.name} from ${urgency.city} booked this ${urgency.minutesAgo} minutes ago`,
      `${NAMES[(seedFrom(vehicle.id) + 1) % NAMES.length]} from ${CITIES[(seedFrom(vehicle.id) + 2) % CITIES.length]} booked a similar vehicle earlier today`,
      `${NAMES[(seedFrom(vehicle.id) + 3) % NAMES.length]} from ${CITIES[(seedFrom(vehicle.id) + 4) % CITIES.length]} viewed this ${Math.max(1, urgency.minutesAgo - 3)} minutes ago`,
    ];
  }, [vehicle, urgency]);
  const [tickerIndex, setTickerIndex] = useState(0);
  useEffect(() => {
    if (!ticker.length) return;
    const t = setInterval(() => setTickerIndex((i) => (i + 1) % ticker.length), 5000);
    return () => clearInterval(t);
  }, [ticker.length]);

  const startReserve = () => {
    if (!user) nav(`/login?next=/vehicles/${id}/reserve`);
    else nav(`/vehicles/${id}/reserve`);
  };

  if (loading) {
    return (
      <div className="container-x section-y grid lg:grid-cols-12 gap-8">
        <div className="lg:col-span-7">
          <Skeleton className="w-full aspect-[16/10] rounded-2xl" />
          <div className="grid grid-cols-4 gap-3 mt-3">
            {[0,1,2,3].map(i => <Skeleton key={i} className="aspect-[16/10] rounded-xl" />)}
          </div>
        </div>
        <div className="lg:col-span-5">
          <Skeleton className="h-8 w-2/3 mb-3" />
          <Skeleton className="h-4 w-1/3 mb-6" />
          <Skeleton className="h-40 w-full rounded-2xl" />
        </div>
      </div>
    );
  }

  if (!vehicle) {
    return (
      <div className="container-x section-y text-center">
        <p className="text-muted-foreground">Vehicle not found.</p>
        <Button asChild variant="outline" className="mt-4"><Link to="/vehicles">Back to browse</Link></Button>
      </div>
    );
  }

  const savings7 = Math.max(0, (vehicle.price_weekly || 0) - (vehicle.member_price_weekly || 0));
  const savings30 = Math.max(0, (vehicle.price_monthly || 0) - (vehicle.member_price_monthly || 0));

  return (
    <div className="bg-background" data-testid="vehicle-detail-page">
      <div className="container-x pt-8 pb-4">
        <div className="text-xs text-muted-foreground">
          <Link to="/vehicles" className="hover:text-foreground">Browse</Link> › <span>{CATEGORY_LABELS[vehicle.category]}</span> › <span className="text-foreground">{vehicle.name}</span>
        </div>
      </div>
      <div className="container-x pb-16 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7">
          <div className="card-clean overflow-hidden bg-white">
            <div className="aspect-[16/10] bg-[hsl(var(--charcoal-100))] flex items-center justify-center">
              <img
                src={mainImg}
                alt={vehicle.name}
                className={`w-full h-full ${((mainImg || "").toLowerCase().includes("remove_bg") || (mainImg || "").toLowerCase().includes("curbo") || ((mainImg || "").toLowerCase().endsWith(".png") && !(mainImg || "").toLowerCase().includes("unsplash"))) ? "object-contain p-8 drop-shadow-[0_28px_36px_rgba(15,23,42,0.14)]" : "object-cover"}`}
                data-testid="vehicle-detail-image"
                onError={(e) => { e.currentTarget.src = "/brand/alfadrive-lion-transparent.png"; }}
              />
            </div>
          </div>
          {(vehicle.gallery && vehicle.gallery.length > 0) && (
            <div className="grid grid-cols-4 gap-3 mt-3">
              {[vehicle.image_url, ...vehicle.gallery.filter(g => g !== vehicle.image_url)].slice(0, 4).map((g, i) => (
                <button key={i} onClick={() => setMainImg(g)} className={`rounded-xl overflow-hidden border bg-white ${mainImg === g ? "border-[hsl(var(--primary))]" : "border-border"}`}>
                  <img src={g} alt="" className="w-full aspect-[16/10] object-contain p-2" />
                </button>
              ))}
            </div>
          )}

          <div className="mt-8">
            <div className="flex flex-wrap gap-2">
              {(vehicle.urgency_tags || []).map((t) => (
                <span key={t} className="inline-flex items-center gap-1 rounded-full px-2 py-1 text-[11px] font-medium badge-blue">
                  <Sparkles className="h-3 w-3" /> {t}
                </span>
              ))}
            </div>
            <h1 className="font-display text-3xl sm:text-4xl font-semibold mt-3 tracking-tight" data-testid="vehicle-detail-title">{vehicle.name}</h1>
            <div className="mt-2 flex items-center gap-4 text-sm text-muted-foreground">
              <span className="inline-flex items-center gap-1"><Star className="h-4 w-4 fill-[hsl(var(--gold-stamp))] text-[hsl(var(--gold-stamp))]" /> {vehicle.rating?.toFixed?.(1)} ({vehicle.reviews_count} reviews)</span>
              <span>{CATEGORY_LABELS[vehicle.category]}</span>
            </div>

            {/* Social urgency block */}
            <div className="mt-5 card-clean p-4 sm:p-5" data-testid="vehicle-detail-social-urgency">
              <div className="flex flex-wrap items-center gap-3">
                <span className="inline-flex items-center gap-2 badge-urgency rounded-full px-3 py-1 text-xs" data-testid="vehicle-detail-urgency-viewers">
                  <span className="pulse-dot" aria-hidden />
                  <Eye className="h-3.5 w-3.5" />
                  {urgency.viewers} people are viewing this vehicle right now
                </span>
                <span className="inline-flex items-center gap-2 badge-urgency rounded-full px-3 py-1 text-xs" data-testid="vehicle-detail-urgency-weekly-bookings">
                  <Flame className="h-3.5 w-3.5" />
                  Booked {urgency.weekly} times this week
                </span>
              </div>
              <div className="mt-3 h-6 relative overflow-hidden" data-testid="vehicle-detail-recent-booking-ticker">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={tickerIndex}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    transition={{ duration: 0.2 }}
                    className="absolute inset-0 flex items-center gap-2 text-xs text-muted-foreground"
                  >
                    <Clock className="h-3.5 w-3.5 text-[hsl(var(--primary))]" />
                    <span>{ticker[tickerIndex]}</span>
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>

            <p className="mt-6 text-foreground max-w-2xl leading-relaxed">{vehicle.description}</p>

            <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="vehicle-detail-spec-grid">
              {[
                { icon: Users, k: "Seats", v: vehicle.seats },
                { icon: Gauge, k: "Transmission", v: vehicle.transmission },
                { icon: Fuel, k: "Fuel", v: vehicle.fuel_type },
                { icon: ShieldCheck, k: "Drivetrain", v: vehicle.drivetrain },
              ].map((s, i) => (
                <div key={i} className="card-clean p-4">
                  <s.icon className="h-4 w-4 text-[hsl(var(--primary))]" />
                  <div className="mt-2 text-[11px] uppercase tracking-widest text-muted-foreground">{s.k}</div>
                  <div className="font-medium">{s.v}</div>
                </div>
              ))}
            </div>

            {(vehicle.features && vehicle.features.length > 0) && (
              <div className="mt-8">
                <h3 className="font-display text-xl font-semibold">Features</h3>
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {vehicle.features.map((f) => (
                    <div key={f} className="flex items-center gap-2 text-sm text-muted-foreground">
                      <CheckCircle2 className="h-4 w-4 text-[hsl(var(--primary))]" /> {f}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-8 card-clean p-6">
              <h3 className="font-display text-xl font-semibold">Rental terms preview</h3>
              <ul className="mt-3 text-sm text-muted-foreground space-y-2">
                <li>Refundable deposit: <span className="font-medium text-foreground">{currency(vehicle.deposit)}</span></li>
                <li>Valid driver’s licence required (verified in your portal)</li>
                <li>Members can use AlfaDrive protection or their own eligible coverage</li>
                <li>{vehicle.mileage}</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="lg:col-span-5">
          <div className="sticky top-24 space-y-4" data-testid="pricing-comparison">
            <div className="card-clean p-4 flex items-center justify-between">
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Availability</div>
              <span className={`text-xs px-2 py-1 rounded-full ${vehicle.available ? "badge-availability" : "badge-danger"}`}>{vehicle.available ? "Available now" : "Unavailable"}</span>
            </div>

            <BookingPlanner vehicle={vehicle} isMember={!!user?.is_member} user={user} />

            <div className="card-clean p-5">
              <div className="flex items-center gap-2"><KeyRound className="h-4 w-4 text-[hsl(var(--primary))]" /><h4 className="font-display font-semibold">Member savings snapshot</h4></div>
              <p className="text-sm text-muted-foreground mt-1" data-testid="pricing-savings-text">
                Drive Alfa Club members save up to {currency(savings7)}/week · {currency(savings30)}/month, plus unlock hourly and short-term (1–6 day) rentals.
              </p>
              {!user?.is_member && (
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button asChild variant="outline" className="rounded-xl" data-testid="detail-join-club"><Link to="/drive-alfa-club">Join Drive Alfa Club</Link></Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
